import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CREATE_WORKSPACE_PATH, SIGNUP_PATH } from "../../utils/constants/routes";
import { saveSignupState, loadSignupState } from "../../utils/helpers/signupState";
import Button from "../../components/Buttons/Button";
import ErrorMessage from "../../components/Messages/ErrorMessage";
import SkeletonLoader from "../../components/Loaders/SkeletonLoader";
import { GET, POST } from "../../Apis/apiWrapper";
import { signupEndpoints, paymentEndpoints } from "../../utils/constants/httpConstants";
import CustomEventEmitter from "../../utils/emitter/EventEmitter";
import { EMITTER_SUCCESS } from "../../utils/constants";
import "./onboarding.css";

// Step header — sticky top bar showing brand only (progress bar/label removed)
const StepHeader = ({ stepNumber, totalSteps = 5 }) => {
  // pct kept in case progress bar is re-enabled
  const pct = Math.round((stepNumber / totalSteps) * 100); // eslint-disable-line no-unused-vars
  return (
    <div className="onb-step-header">
      <div className="onb-step-header-brand">
        <img src="/assets/images/new_logo_reachiq.svg" alt="ReachIQ" className="onb-step-brand-logo" />
      </div>
      {/* onb-step-header-right (label + progress bar + %) removed per product request */}
    </div>
  );
};

// Stepper component shared across new screens
const Stepper = ({ current }) => {
  const steps = ["Sign Up", "Choose Plan", "Subdomain", "Verify Email"];
  return (
    <div className="onb-stepper">
      {steps.map((label, i) => {
        const step = i + 1;
        const isDone   = step < current;
        const isActive = step === current;
        return (
          <React.Fragment key={label}>
            <div className={`onb-st ${isDone ? "done" : ""} ${isActive ? "active" : ""}`}>
              <div className="onb-st-n">
                {isDone ? (
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" aria-hidden="true" focusable="false">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : step}
              </div>
              <span>{label}</span>
            </div>
            {i < steps.length - 1 && <div className="onb-st-ln" />}
          </React.Fragment>
        );
      })}
    </div>
  );
};


// Transform raw API packages into the 2-card display format (Standard + Growth).
// The Free plan is intentionally NOT shown during onboarding (hard business rule 2026-07-03;
// the backend public-list also filters it) — it stays available from inside the platform.
function buildDisplayPlans(packages) {
  // Build feature rows directly from the API's features object — no hardcoded keys
  const mkFeatures = (pkg) =>
    Object.entries(pkg.features || {}).map(([key, val]) => ({
      label: key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
      cost:  `${val?.credits ?? 0} cr`,
      limit: (val?.upto ?? 0).toLocaleString(),
    }));

  // Strip " - Monthly" / " - Annual" suffix from API name for card display
  const baseName = (n) => n.replace(/\s*[-–]\s*(monthly|annual|annually|yearly|year|month)$/i, "").trim();

  // Treat both "month" and "monthly" as monthly; "year"/"annual"/"yearly" as yearly
  const isMonthly = (p) => p.billing_period === "month" || p.billing_period === "monthly";
  const isYearly  = (p) => p.billing_period === "year"  || p.billing_period === "annual" || p.billing_period === "yearly";

  const stdMo    = packages.find(p => !p.isFree && isMonthly(p) && p.name.toLowerCase().includes("standard"));
  const stdYr    = packages.find(p => !p.isFree && isYearly(p)  && p.name.toLowerCase().includes("standard"));
  const growthMo = packages.find(p => !p.isFree && isMonthly(p) && p.name.toLowerCase().includes("growth"));
  const growthYr = packages.find(p => !p.isFree && isYearly(p)  && p.name.toLowerCase().includes("growth"));

  return [
    stdMo && {
      key: "standard", isFree: false, popular: false,
      _idMo: stdMo._id, _idYr: stdYr?._id || stdMo._id,
      priceIdMo: stdMo.stripe_price_id || null,
      priceIdYr: stdYr?.stripe_price_id || null,
      name: baseName(stdMo.name),
      currency: stdMo.currency || "usd",
      priceMo: stdMo.amount,
      priceYr: stdYr?.amount ?? stdMo.amount,
      creditsMo: (stdMo.credits || 0).toLocaleString(),
      creditsYr: (stdYr?.credits || stdMo.credits || 0).toLocaleString(),
      features: mkFeatures(stdMo),
      featuresYr: stdYr ? mkFeatures(stdYr) : null,
    },
    growthMo && {
      key: "growth", isFree: false, popular: true,
      _idMo: growthMo._id, _idYr: growthYr?._id || growthMo._id,
      priceIdMo: growthMo.stripe_price_id || null,
      priceIdYr: growthYr?.stripe_price_id || null,
      name: baseName(growthMo.name),
      currency: growthMo.currency || "usd",
      priceMo: growthMo.amount,
      priceYr: growthYr?.amount ?? growthMo.amount,
      creditsMo: (growthMo.credits || 0).toLocaleString(),
      creditsYr: (growthYr?.credits || growthMo.credits || 0).toLocaleString(),
      features: mkFeatures(growthMo),
      featuresYr: growthYr ? mkFeatures(growthYr) : null,
    },
  ].filter(Boolean);
}

// ── Internal QA test plan (hidden $1 plan) ─────────────────────────────────────
// A $1/mo catalog plan named exactly QA_PLAN_NAME is normally invisible here (buildDisplayPlans
// only renders "standard"/"growth"). A faint top-right control gates it behind an access code; on
// unlock its card is appended so QA can run the real trial / buy-now flow on prod at ~$1. Keep the
// name in sync with the catalog doc AND the in-app plan-grid hide filter.
const QA_PLAN_NAME = "QA Dollar Test";
const QA_ACCESS_HASH = -708047054; // djb2 of the access code — plaintext is never shipped
const djb2 = (s) => { let x = 5381; for (let i = 0; i < s.length; i++) { x = ((x << 5) + x) + s.charCodeAt(i); x |= 0; } return x; };

function buildQaPlan(packages) {
  const p = packages.find(
    (x) => !x.isFree && (x.name || "").trim().toLowerCase() === QA_PLAN_NAME.toLowerCase(),
  );
  if (!p) return null;
  const credits = (p.credits || 0).toLocaleString();
  return {
    key: "qa", isFree: false, popular: false, isQa: true,
    _idMo: p._id, _idYr: p._id,
    priceIdMo: p.stripe_price_id || null,
    priceIdYr: p.stripe_price_id || null,
    name: p.name,
    currency: p.currency || "usd",
    priceMo: p.amount, priceYr: p.amount,
    creditsMo: credits, creditsYr: credits,
    features: [], featuresYr: null,
  };
}

// Fixed feature rows shown on every plan card
const PLAN_FEATURES = [
  { label: "ICP scoring",    credits: 10 },
  { label: "Personalisation", credits: 2 },
  { label: "Verified Email",  credits: 1 },
];

// Checkmark SVG
const Check = ({ color = "#fff" }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" width="10" height="10" aria-hidden="true" focusable="false">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const PlanScreen = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const userData  = { ...loadSignupState(), ...(location.state || {}) };

  // Billing preset from the marketing-site preselection (annual → "yr"), default monthly.
  const [billing, setBilling]           = useState(userData.preselected_billing === "yr" ? "yr" : "mo"); // "mo" | "yr"
  const [planType, setPlanType]         = useState("tools"); // "tools" | "dfy"
  const [plans, setPlans]               = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [busyKey, setBusyKey]           = useState(null);   // which plan card's CTA is in-flight
  const [planError, setPlanError]       = useState(location.state?.error || "");
  const [showAllPlans, setShowAllPlans] = useState(false); // "Switch plan" pressed — leave the preselect view

  // Trial gating: the 7-day-trial CTA only exists for users who arrived via the invite link
  // /auth/signup/free-trial (SignupScreen stamps this flag); plain /auth/signup is buy-now only.
  const trialOffer = (() => {
    try { return sessionStorage.getItem("riq_trial_offer") === "1"; } catch (_) { return false; }
  })();
  const [qaPlan, setQaPlan]             = useState(null);   // hidden $1 QA test plan, if the catalog has it
  const [qaUnlocked, setQaUnlocked]     = useState(() => {
    try { return sessionStorage.getItem("riq_qa_unlock") === "1"; } catch (_) { return false; }
  });
  const [dotHover, setDotHover]         = useState(false); // faint QA gate reveals on hover

  // "Talk to Sales" (Done-for-You) — one-click intent capture, no form/popup. The backend
  // already holds this person's signup details on the draft; the click just sends draft_id
  // and sales gets notified internally. Ref latch = no duplicate send on a double-click.
  const [salesBusy, setSalesBusy]   = useState(false);
  const [salesDone, setSalesDone]   = useState(false);
  const [salesErr, setSalesErr]     = useState("");
  const salesInFlight = useRef(false);

  const submitSalesLead = async () => {
    if (salesInFlight.current || salesDone) return;
    setSalesErr("");
    if (!userData.draft_id) {
      setSalesErr("Your signup session expired. Please start again.");
      navigate(SIGNUP_PATH, { replace: true });
      return;
    }
    salesInFlight.current = true;
    setSalesBusy(true);
    try {
      await POST(signupEndpoints.salesLead, { draft_id: userData.draft_id });
      setSalesDone(true);
    } catch (err) {
      setSalesErr(err?.data?.message || err?.response?.data?.message || "Could not submit right now. Please try again.");
    } finally {
      salesInFlight.current = false;
      setSalesBusy(false);
    }
  };

  // Durable state: bounce to signup if we have no draft_id (hard refresh / Back / direct URL with
  // nothing persisted); otherwise keep the persisted signup state current for later-step rehydration.
  useEffect(() => {
    if (!userData.draft_id) { navigate(SIGNUP_PATH, { replace: true }); return; }
    saveSignupState(userData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch live plans on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await GET(paymentEndpoints.publicPackages);
        const packages = res?.data?.data?.packages || [];
        const display = buildDisplayPlans(packages);
        if (display.length) setPlans(display);
        setQaPlan(buildQaPlan(packages));
      } catch {
        // plans stays empty on API error
      } finally {
        setPlansLoading(false);
      }
    })();
  }, []);

  const getPrice    = (plan) => billing === "yr" ? plan.priceYr : plan.priceMo;
  const getPlanId   = (plan) => billing === "yr" ? plan._idYr   : plan._idMo;

  // Trial rollout: the date a card auto-charges if the user starts a 7-day trial today.
  const trialChargeDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
    .toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

  // Marketing-site preselection: show ONLY the chosen plan until "Switch plan" is pressed.
  // If the key doesn't match the live catalog (plan renamed/removed), fall back to all plans.
  const preselectKey = !showAllPlans ? (userData.preselected_plan || null) : null;
  const preselected  = preselectKey && !plansLoading ? (plans.find((p) => p.key === preselectKey) || null) : null;
  const singleView   = !!preselectKey && (plansLoading || !!preselected);
  const visiblePlans = preselected ? [preselected] : plans;
  // Append the hidden QA plan once the access code has been entered.
  const displayCards = (qaUnlocked && qaPlan) ? [...visiblePlans, qaPlan] : visiblePlans;

  // Faint top-right control → prompt for the access code → reveal the hidden $1 QA plan.
  const openQaGate = () => {
    if (qaUnlocked) return;
    const code = window.prompt("Access code");
    if (code == null) return;
    if (djb2(code) === QA_ACCESS_HASH) {
      try { sessionStorage.setItem("riq_qa_unlock", "1"); } catch (_) {}
      setQaUnlocked(true);
    }
  };

  const switchPlan = () => {
    setShowAllPlans(true);
    // Clear the persisted preselection so a refresh also lands on the full chooser.
    const { preselected_plan, preselected_billing, ...rest } = loadSignupState();
    saveSignupState(rest);
  };

  // Selecting a paid plan goes STRAIGHT to Stripe — no intermediate Order Summary
  // modal. The card's CTA shows its own busy state (busyKey === plan.key) while we
  // register the plan and create the checkout session, then we redirect.
  const handleSelectPaid = async (plan, opts = {}) => {
    const wantTrial = opts.trial === true;
    if (busyKey) return;
    setPlanError("");
    setBusyKey(plan.key + (wantTrial ? "|trial" : "|buy"));
    try {
      // Fix #2: never start a paid checkout without a draft_id — it would POST draft_id:undefined
      // and orphan the Stripe session. Durable state should keep it present; if it's gone, restart.
      if (!userData.draft_id) {
        setPlanError("Your signup session expired. Please start again.");
        setBusyKey(null);
        navigate(SIGNUP_PATH, { replace: true });
        return;
      }
      const packageId = getPlanId(plan);
      const priceId   = billing === "yr" ? plan.priceIdYr : plan.priceIdMo;
      // Fix #3: don't POST a null price_id (e.g. this plan has no yearly price configured) — Stripe
      // would reject it / orphan the session.
      if (!priceId) {
        setPlanError("This plan isn't available for the selected billing cycle. Please choose another.");
        setBusyKey(null);
        return;
      }

      // Register the plan selection on the draft
      if (packageId && userData.draft_id) {
        await POST(signupEndpoints.setPlan, {
          draft_id:   userData.draft_id,
          package_id: packageId,
        });
      }

      // Create a Stripe checkout session. A trial checkout must carry the invite token that
      // unlocked the CTA — the backend enforces (and later redeems) it.
      let trialToken = "";
      try { trialToken = sessionStorage.getItem("riq_trial_token") || ""; } catch (_) {}
      const res = await POST(paymentEndpoints.createSession, {
        draft_id: userData.draft_id,
        price_id: priceId,
        trial: wantTrial,   // true = 7-day free trial ($0 today); false = buy now (charge immediately)
        trial_token: wantTrial ? trialToken : undefined,
      });
      const data        = res?.data?.data || {};
      const checkoutUrl = data.checkout_url;

      // Already-paid case: the user has an active subscription for this draft, so the
      // backend resumes the existing payment instead of opening a new Stripe checkout
      // (no checkout_url is returned, e.g. { resumed: true }). Don't treat this as an
      // error — confirm with a success toast and continue straight to the next step
      // (subdomain/workspace setup), just like the free-plan path.
      if (!checkoutUrl) {
        if (data.resumed) {
          CustomEventEmitter.emit(
            EMITTER_SUCCESS,
            res?.data?.message || "Your payment is already active. Continuing to workspace setup.",
          );
          navigate(CREATE_WORKSPACE_PATH, {
            state: { ...userData, plan: plan.key, price: getPrice(plan), billing },
          });
          return;
        }
        // No checkout URL and not a resume — genuinely couldn't start checkout.
        throw new Error("No checkout URL returned");
      }

      // Persist everything needed to resume after the Stripe redirect
      // Fix #6: strip the cleartext password before persisting to sessionStorage (unused downstream).
      const { password, confirmPassword, ...resumeData } = userData;
      sessionStorage.setItem("signup_resume", JSON.stringify({
        ...resumeData,
        plan:    plan.key,
        billing,
      }));

      window.location.href = checkoutUrl; // → Stripe hosted checkout
    } catch (err) {
      // Already-subscribed: if the backend rejects because this draft/email already has an active
      // plan/payment, don't show a dead-end "couldn't start checkout" error — continue to the next
      // step (the BE resumes + links the existing subscription), same as the {resumed:true} path.
      const msg = (err?.data?.message || err?.message || "").toLowerCase();
      const alreadyActive = msg.includes("already") &&
        (msg.includes("active") || msg.includes("plan") || msg.includes("payment") || msg.includes("subscription") || msg.includes("exist"));
      if (alreadyActive) {
        CustomEventEmitter.emit(EMITTER_SUCCESS, "You already have an active plan. Continuing to workspace setup.");
        navigate(CREATE_WORKSPACE_PATH, {
          state: { ...userData, plan: plan.key, price: getPrice(plan), billing },
        });
        return;
      }
      setPlanError(err?.data?.message || "Could not start checkout. Please try again.");
      setBusyKey(null);
    }
  };

  return (
    <div className="onb-page">
      {/* Faint QA gate (top-right corner). Reveals the internal $1 test plan after an access code.
          Kept intentionally near-invisible so real signups never notice it. */}
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        onClick={openQaGate}
        onMouseEnter={() => setDotHover(true)}
        onMouseLeave={() => setDotHover(false)}
        style={{
          // z-index must clear the sticky StepHeader (z-index:10) or the header covers it.
          position: "fixed", top: 4, right: 6, zIndex: 9999,
          width: 16, height: 16, padding: 0, border: "none", background: "transparent",
          color: "#9aa3af", opacity: qaUnlocked ? 0 : (dotHover ? 0.85 : 0.12), cursor: "pointer",
          fontSize: 14, lineHeight: "16px", transition: "opacity 0.15s ease",
        }}
      >
        •
      </button>
      <StepHeader stepNumber={2} />
      <Stepper current={2} />

      {/* ── Unified plan outer: header + tab toggle always visible; content switches by tab ── */}
      <div className="onb-plan-outer">
        <div className="onb-plan-hd">
          <h2>Choose Your Plan</h2>
          <p>Select the plan that best fits your needs. You can upgrade anytime.</p>
        </div>

        {/* Controls row — plan type toggle always shown; billing cycle only in tools mode */}
        <div className="onb-plan-controls">
          {/* Plan type toggle (segmented control — bespoke, token-driven; not a DS Button).
              Hidden in the preselect single-plan view — "Switch plan" restores it. */}
          {!singleView && (
            <div className="onb-seg" role="group" aria-label="Plan type">
              <button type="button" aria-pressed={planType === "tools"} className={planType === "tools" ? "on" : ""} onClick={() => setPlanType("tools")}>
                Just the Tools
              </button>
              <button type="button" aria-pressed={planType === "dfy"} className={planType === "dfy" ? "on" : ""} onClick={() => setPlanType("dfy")}>
                Done for You <span className="onb-badge-save">NEW</span>
              </button>
            </div>
          )}

          {/* Billing cycle toggle — only shown under "Just the Tools" */}
          {planType === "tools" && (
            <>
              <div className="onb-bc-row">
                <span>Billing Cycle</span>
                <div className="onb-seg" role="group" aria-label="Billing cycle">
                  <button type="button" aria-pressed={billing === "mo"} className={billing === "mo" ? "on" : ""} onClick={() => setBilling("mo")}>
                    Monthly
                  </button>
                  <button type="button" aria-pressed={billing === "yr"} className={billing === "yr" ? "on" : ""} onClick={() => setBilling("yr")}>
                    Yearly <span className="onb-badge-save">SAVE 20%</span>
                  </button>
                </div>
              </div>
              {billing === "yr" && (
                <div style={{ fontSize: "12.5px", color: "#8a8d93", margin: "6px 2px 0", textAlign: "right" }}>
                  Coupon codes are valid for monthly plans only.
                </div>
              )}
            </>
          )}
          {singleView && (
            <button type="button" className="onb-switch-link" onClick={switchPlan}>
              Switch plan
            </button>
          )}
        </div>

        {/* ── Just the Tools: plan cards ── */}
        {planType === "tools" && (
          <>
            {plansLoading ? (
              <div className={`onb-plans ${singleView ? "onb-plans--single" : ""}`}>
                {(singleView ? [1] : [1, 2]).map((i) => (
                  <div key={i} className="onb-pc" style={{ gap: "10px" }}>
                    {/* Each SkeletonLoader's inner <Stack> defaults to height:100%, which
                        in this flex-column card makes every row claim the full card height
                        and spill outside the border. Pin each Stack's height to its own
                        skeletonHeight so the rows stack naturally within the card. */}
                    <SkeletonLoader type="text" skeletonHeight={22} skeletonWidth="55%" stackHeight="22px" />
                    <SkeletonLoader type="text" skeletonHeight={14} skeletonWidth="40%" stackHeight="14px" />
                    <SkeletonLoader type="rectangular" skeletonHeight={52} skeletonWidth="70%" stackHeight="52px" />
                    <SkeletonLoader type="text" skeletonHeight={14} skeletonWidth="80%" stackHeight="14px" />
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px", margin: "6px 0 4px" }}>
                      <SkeletonLoader type="text" skeletonHeight={10} skeletonWidth="100%" stackHeight="10px" />
                      <SkeletonLoader type="text" skeletonHeight={16} skeletonWidth="100%" stackHeight="16px" />
                      <SkeletonLoader type="text" skeletonHeight={16} skeletonWidth="100%" stackHeight="16px" />
                      <SkeletonLoader type="text" skeletonHeight={16} skeletonWidth="100%" stackHeight="16px" />
                    </div>
                    <SkeletonLoader type="rectangular" skeletonHeight={40} skeletonWidth="100%" stackHeight="40px" />
                  </div>
                ))}
              </div>
            ) : (
              <div className={`onb-plans ${singleView ? "onb-plans--single" : ""}`}>
                {displayCards.map((plan) => (
                  <div key={plan.key} className={`onb-pc ${plan.popular ? "pop" : ""}`}>
                    {plan.popular && <div className="onb-pop-badge">MOST POPULAR</div>}
                    <div className="onb-pc-name">{plan.name}</div>
                    <div className="onb-pc-billing">{billing === "yr" ? `Yearly · ${(plan.currency || "usd").toUpperCase()}` : `Monthly · ${(plan.currency || "usd").toUpperCase()}`}</div>
                    <div className="onb-pc-price">
                      <><sup>$</sup>{getPrice(plan)}<span>{billing === "yr" ? "/yr" : "/mo"}</span></>
                    </div>
                    <div className="onb-pc-cred">TOTAL CREDITS: {billing === "yr" ? plan.creditsYr : plan.creditsMo}</div>

                    <table className="onb-ft">
                      <thead>
                        <tr>
                          <th style={{ textAlign: "left" }}>What each action costs</th>
                          <th>Credits</th>
                        </tr>
                      </thead>
                      <tbody>
                        {PLAN_FEATURES.map((f) => (
                          <tr key={f.label}>
                            <td>
                              <span style={{ display: "inline-block", width: "7px", height: "7px", borderRadius: "50%", backgroundColor: "#ff4848", marginRight: "7px", verticalAlign: "middle", flexShrink: 0 }} />
                              {f.label}
                            </td>
                            <td>{f.credits}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    <div className="onb-pc-cta">
                      {trialOffer && (
                        <>
                          <button
                            type="button"
                            className="onb-pc-btn onb-pc-btn--primary"
                            disabled={!!busyKey}
                            onClick={() => handleSelectPaid(plan, { trial: true })}
                          >
                            {busyKey === plan.key + "|trial" ? "Please wait…" : "Start 7-day free trial"}
                          </button>
                          <div className="onb-pc-trial-note">
                            $0 today, then <strong>${getPrice(plan)}{billing === "yr" ? "/yr" : "/mo"}</strong> on {trialChargeDate}. Cancel anytime.
                          </div>
                          <div className="onb-pc-or"><span>or</span></div>
                        </>
                      )}
                      <button
                        type="button"
                        className={trialOffer ? "onb-pc-btn onb-pc-btn--ghost" : "onb-pc-btn onb-pc-btn--primary"}
                        disabled={!!busyKey}
                        onClick={() => handleSelectPaid(plan, { trial: false })}
                      >
                        {busyKey === plan.key + "|buy" ? "Please wait…" : `Buy now · $${getPrice(plan)} today`}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {planError && (
              <div role="alert" style={{ textAlign: "center", marginTop: "12px" }}>
                <ErrorMessage message={planError} marginBottom="0" fontSize="14px" />
              </div>
            )}
          </>
        )}

        {/* ── Done for You ── */}
        {planType === "dfy" && (
          <div className="onb-dfy-wrap">
            <div className="onb-dfy-plan-card">
              <div className="onb-dfy-plan-badge">Managed Service</div>
              <h2 className="onb-dfy-plan-h">Done for You</h2>
              <p className="onb-dfy-plan-desc">
                We run your outbound. You take the meetings. Dedicated SDR plus the platform plus
                the sender infrastructure, on one bill, outbound running by month 2.
              </p>
              <div className="onb-dfy-plan-price-row">
                <span className="onb-dfy-plan-price-val">Custom</span>
                <span className="onb-dfy-plan-price-label">priced per program</span>
              </div>
              <div className="onb-dfy-plan-price-sub">Tailored to your ICP, volume, and goals.</div>

              <div className="onb-dfy-plan-stats">
                <div className="onb-dfy-plan-stat">
                  <div className="onb-dfy-plan-stat-val">5 to 15</div>
                  <div className="onb-dfy-plan-stat-lbl">Qualified meetings / month, after a 2-month ramp</div>
                </div>
                <div className="onb-dfy-plan-stat">
                  <div className="onb-dfy-plan-stat-val">85%+</div>
                  <div className="onb-dfy-plan-stat-lbl">Typical show rate once warm</div>
                </div>
              </div>

              <div className="onb-dfy-plan-features">
                {[
                  { bold: "Dedicated SDR", rest: " working full-time on your outbound" },
                  { bold: "AI-personalized outreach", rest: ", unique email for every prospect" },
                  { bold: "Multi-channel sequences", rest: ": email, LinkedIn, and call scripts" },
                  { bold: "ICP targeting and data", rest: ", plus full sender infrastructure setup" },
                  { bold: "Qualified meetings booked", rest: " on your calendar, with weekly reporting" },
                ].map((f, i) => (
                  <div className="onb-dfy-plan-feat" key={i}>
                    <div className="onb-dfy-plan-feat-chk"><Check /></div>
                    <span><strong>{f.bold}</strong>{f.rest}</span>
                  </div>
                ))}
              </div>

              <div className="onb-dfy-plan-note">
                <strong>3-month minimum pilot</strong>, then month to month. Concrete success metrics agreed up front.
              </div>

              <Button
                type="button"
                variant="primary"
                size="lg44"
                label={salesDone ? "Thanks! Our team will reach out." : "Talk to Sales"}
                isLoading={salesBusy}
                disabled={salesBusy || salesDone}
                onClick={submitSalesLead}
              />
              {salesErr && (
                <div role="alert" style={{ marginTop: "12px" }}>
                  <ErrorMessage message={salesErr} marginBottom="0" fontSize="14px" />
                </div>
              )}
              {/* Back button removed per product request */}
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export { Stepper, StepHeader };
export default PlanScreen;
